/**
 * Cart drawer empty ↔ filled transitions need a full section swap.
 * Runs in capture phase so the drawer updates before paint.
 */
const CART_SECTION_ID = 'cart-drawer';

/**
 * @returns {number}
 */
export function getCartItemCount() {
  const text =
    document.querySelector('.header-actions__cart-icon .cart-bubble__text')?.textContent || '';
  const matches = text.match(/\d+/g);
  if (!matches?.length) return 0;
  return Math.max(...matches.map((n) => parseInt(n, 10)));
}

/**
 * @param {string} html
 * @returns {boolean}
 */
export function sectionHtmlIsEmpty(html) {
  return (
    html.includes('cart-drawer--empty') ||
    html.includes('cart-drawer__heading--empty') ||
    html.includes('cart-items__empty')
  );
}

/**
 * @returns {boolean}
 */
export function isCartDrawerUiEmpty() {
  const section = document.getElementById(`shopify-section-${CART_SECTION_ID}`);
  if (!section) return false;

  return Boolean(
    section.querySelector('.cart-drawer__heading--empty, .cart-items__empty') ||
      section.querySelector('.cart-drawer--empty')
  );
}

/**
 * @param {string} html
 */
export function replaceCartDrawerSection(html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existing = document.getElementById(`shopify-section-${CART_SECTION_ID}`);
  const updated = fragment.getElementById(`shopify-section-${CART_SECTION_ID}`);

  if (!existing || !updated) return false;

  const dialogWasOpen = existing.querySelector('dialog[open]') instanceof HTMLDialogElement;
  existing.innerHTML = updated.innerHTML;

  if (dialogWasOpen) {
    const dialog = existing.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      try {
        dialog.showModal();
      } catch (_) {
        dialog.open = true;
      }
    }
  }

  return true;
}

/**
 * Flag in-flight add; only open immediately when the cart already has items.
 * @param {Element | null} cartDrawer
 */
export function prepareCartDrawerForAdd(cartDrawer) {
  if (!(cartDrawer instanceof HTMLElement)) return;

  cartDrawer.classList.add('cart-drawer--pending');

  if (getCartItemCount() > 0 && 'showDialog' in cartDrawer) {
    try {
      /** @type {any} */ (cartDrawer).showDialog();
    } catch (_) {
      // ignore
    }
  }
}

/**
 * Open the drawer after the first item lands (empty → filled).
 * @param {Element | null} cartDrawer
 */
function openCartDrawerAfterFirstAdd(cartDrawer) {
  if (!(cartDrawer instanceof HTMLElement)) return;

  cartDrawer.classList.remove('cart-drawer--pending');

  if (!('showDialog' in cartDrawer)) return;

  const dialog = cartDrawer.querySelector('dialog');
  if (dialog instanceof HTMLDialogElement && dialog.open) return;

  try {
    /** @type {any} */ (cartDrawer).showDialog();
  } catch (_) {
    if (dialog instanceof HTMLDialogElement) {
      dialog.open = true;
    }
  }
}

/**
 * @param {CustomEvent} event
 */
function handleCartUpdate(event) {
  const data = event.detail?.data;
  if (!data) return;

  const sectionHtml = data.sections?.[CART_SECTION_ID];
  const cartDrawer = document.querySelector('cart-drawer-component[auto-open]');

  if (sectionHtml) {
    const wasEmpty = isCartDrawerUiEmpty();
    const willBeEmpty = sectionHtmlIsEmpty(sectionHtml);

    if (wasEmpty !== willBeEmpty) {
      replaceCartDrawerSection(sectionHtml);

      if (wasEmpty && !willBeEmpty) {
        openCartDrawerAfterFirstAdd(cartDrawer);
      } else {
        cartDrawer?.classList.remove('cart-drawer--pending');
      }
      return;
    }
  }

  cartDrawer?.classList.remove('cart-drawer--pending');
}

document.addEventListener('cart:update', handleCartUpdate, true);
